import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, API_BASE_URL } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { avatarUrl } from '@/lib/avatar-url';
import { AVATAR_PALETTE } from '@/lib/avatar-palette';
import { useAuth } from '@/lib/use-auth';
import type { AuthUser } from '@/lib/auth-context';
import { AvatarCropModal } from '@/components/AvatarCropModal';

interface MySettings {
  notifyByEmail: boolean;
}

export function Settings() {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['me', 'settings'],
    queryFn: () => api<MySettings>('/me/settings'),
  });

  const [notifyByEmail, setNotifyByEmail] = useState(true);
  const [savedFlag, setSavedFlag] = useState(false);

  useEffect(() => {
    if (settings.data) setNotifyByEmail(settings.data.notifyByEmail);
  }, [settings.data]);

  const update = useMutation({
    mutationFn: (next: Partial<MySettings>) =>
      api<MySettings>('/me/settings', {
        method: 'PATCH',
        body: JSON.stringify(next),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['me', 'settings'], data);
      setSavedFlag(true);
      window.setTimeout(() => setSavedFlag(false), 2000);
    },
  });

  function onToggleEmail(next: boolean) {
    setNotifyByEmail(next);
    update.mutate({ notifyByEmail: next });
  }

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto">
      <header className="mb-6 text-center">
        <h1 className="text-2xl font-semibold tracking-tight2">Ajustes</h1>
        <p className="text-[14px] text-muted-foreground mt-1">
          Tus preferencias en CheckIn. Aquí iremos añadiendo más opciones según las necesitemos.
        </p>
      </header>

      <div className="space-y-5">
        {/* Notificaciones */}
        <Panel
          title="Notificaciones"
          description="Cómo te avisamos de eventos relevantes (solicitudes, decisiones, etc.)."
        >
          {settings.isLoading ? (
            <p className="px-6 py-5 text-sm text-muted-foreground">Cargando…</p>
          ) : settings.isError ? (
            <p role="alert" className="px-6 py-5 text-sm text-destructive">
              {settings.error instanceof ApiError
                ? settings.error.message
                : 'Error al cargar ajustes'}
            </p>
          ) : (
            <>
              <SettingRow
                title="Recibir confirmaciones por email"
                description="Si lo desactivas seguirás viendo las notificaciones dentro de la app (campanita), pero no recibirás email de aviso."
                action={
                  <Toggle
                    checked={notifyByEmail}
                    disabled={update.isPending}
                    onChange={onToggleEmail}
                    ariaLabel="Recibir confirmaciones por email"
                  />
                }
              />
              {update.error ? (
                <div className="px-6 py-3 border-t border-border bg-destructive/5">
                  <p role="alert" className="text-sm text-destructive">
                    {update.error instanceof ApiError ? update.error.message : 'Error al guardar'}
                  </p>
                </div>
              ) : savedFlag ? (
                <div className="px-6 py-3 border-t border-border bg-success/5">
                  <p className="text-[13px] text-success font-medium">Guardado.</p>
                </div>
              ) : null}
            </>
          )}
        </Panel>

        {/* Editar perfil */}
        <ProfilePanel />

        {/* Seguridad */}
        <Panel title="Seguridad" description="Gestión de tu contraseña.">
          <SettingRow
            title="Contraseña"
            description="Cambia tu contraseña actual por una nueva. Necesitarás conocer la actual."
            action={
              <Button asChild variant="outline" size="sm">
                <Link to="/password/change">Cambiar contraseña</Link>
              </Button>
            }
          />
        </Panel>

        {/* Ayuda */}
        <Panel title="Ayuda" description="Manual de uso de la aplicación.">
          <SettingRow
            title="Manual de usuario"
            description="Consulta cómo fichar, pedir ausencias, gestionar tu jornada y más. Con índice, buscador y opción de descargar en PDF."
            action={
              <Button asChild variant="outline" size="sm">
                <Link to="/ayuda">Abrir manual</Link>
              </Button>
            }
          />
        </Panel>
      </div>
    </div>
  );
}

/**
 * Panel "Editar perfil": color del icono (paleta) y foto. Las tres
 * operaciones (color, subir, quitar) devuelven el perfil público completo;
 * con él refrescamos el AuthUser en memoria (`updateUser`) para que el
 * cambio se vea al instante, e invalidamos las vistas de equipo/admin.
 */
function ProfilePanel() {
  const { user, updateUser } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);

  function applyProfile(updated: AuthUser) {
    updateUser(updated);
    void queryClient.invalidateQueries({ queryKey: ['team'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  }

  const colorMutation = useMutation({
    mutationFn: (profileColor: string | null) =>
      api<AuthUser>('/me/profile', {
        method: 'PATCH',
        body: JSON.stringify({ profileColor }),
      }),
    onSuccess: applyProfile,
  });

  const uploadMutation = useMutation({
    mutationFn: async (blob: Blob) => {
      const ext = blob.type === 'image/webp' ? 'webp' : 'jpg';
      const form = new FormData();
      form.append('file', blob, `avatar.${ext}`);
      const res = await fetch(`${API_BASE_URL}/me/avatar`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as {
          message?: string;
        };
        throw new Error(payload.message ?? `Error ${res.status}`);
      }
      return (await res.json()) as AuthUser;
    },
    onSuccess: (updated) => {
      applyProfile(updated);
      setCropFile(null);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => api<AuthUser>('/me/avatar', { method: 'DELETE' }),
    onSuccess: applyProfile,
  });

  if (!user) return null;

  const currentColor = user.profileColor ?? null;
  const hasPhoto = Boolean(user.avatarUpdatedAt);
  const busy = colorMutation.isPending || uploadMutation.isPending || removeMutation.isPending;
  const errorMsg =
    uploadMutation.error instanceof Error
      ? uploadMutation.error.message
      : colorMutation.error instanceof ApiError
        ? colorMutation.error.message
        : removeMutation.error instanceof ApiError
          ? removeMutation.error.message
          : null;

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-elegir el mismo archivo
    if (file) setCropFile(file);
  }

  return (
    <Panel
      title="Editar perfil"
      description="Personaliza tu icono: elige un color o sube una foto. Así te reconocen mejor en el calendario de equipo."
    >
      {/* Foto */}
      <div className="flex items-center gap-4 px-6 py-5">
        <Avatar
          fullName={user.fullName}
          color={user.profileColor}
          avatarUrl={avatarUrl(user.id, user.avatarUpdatedAt)}
          size="lg"
        />
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-medium text-foreground">Foto</p>
          <p className="mt-1 text-[12px] text-muted-foreground leading-relaxed">
            {hasPhoto
              ? 'La foto se muestra en lugar del icono de color.'
              : 'Sube una foto (JPG o WEBP). Podrás encuadrarla antes de guardar.'}
          </p>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
          >
            {hasPhoto ? 'Cambiar' : 'Subir foto'}
          </Button>
          {hasPhoto ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => removeMutation.mutate()}
            >
              Quitar
            </Button>
          ) : null}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPickFile}
        />
      </div>

      {/* Color */}
      <div className="border-t border-border px-6 py-5">
        <p className="text-[14px] font-medium text-foreground">Color del icono</p>
        <p className="mt-1 text-[12px] text-muted-foreground">
          Se usa cuando no tienes foto. {hasPhoto ? 'Quita la foto para verlo.' : ''}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2.5">
          <ColorSwatch
            color={null}
            selected={currentColor === null}
            disabled={busy}
            onSelect={() => colorMutation.mutate(null)}
          />
          {AVATAR_PALETTE.map((color) => (
            <ColorSwatch
              key={color}
              color={color}
              selected={currentColor === color}
              disabled={busy}
              onSelect={() => colorMutation.mutate(color)}
            />
          ))}
        </div>
      </div>

      {errorMsg ? (
        <div className="border-t border-border bg-destructive/5 px-6 py-3">
          <p role="alert" className="text-sm text-destructive">
            {errorMsg}
          </p>
        </div>
      ) : null}

      {cropFile ? (
        <AvatarCropModal
          file={cropFile}
          busy={uploadMutation.isPending}
          onCancel={() => setCropFile(null)}
          onConfirm={(blob) => uploadMutation.mutate(blob)}
        />
      ) : null}
    </Panel>
  );
}

/** Botón de selección de color. `color === null` = "por defecto" (azul). */
function ColorSwatch({
  color,
  selected,
  disabled,
  onSelect,
}: {
  color: string | null;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={color ? `Color ${color}` : 'Color por defecto'}
      title={color ?? 'Por defecto'}
      className={
        'h-8 w-8 rounded-full border border-black/10 transition disabled:opacity-50 ' +
        (selected ? 'ring-2 ring-icaria-600 ring-offset-2' : 'hover:scale-105')
      }
      style={{ backgroundColor: color ?? '#1B23B8' }}
    />
  );
}

function Panel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-card border border-border rounded-sm shadow-sm overflow-hidden">
      <header className="px-6 py-4 border-b border-border">
        <h2 className="text-base font-semibold tracking-tightish">{title}</h2>
        {description ? (
          <p className="text-[13px] text-muted-foreground mt-0.5">{description}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}

function SettingRow({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action: React.ReactNode;
}) {
  return (
    <div className="px-6 py-5 flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium text-foreground">{title}</p>
        {description ? (
          <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">{description}</p>
        ) : null}
      </div>
      <div className="flex-shrink-0">{action}</div>
    </div>
  );
}

function Toggle({
  checked,
  disabled,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        backgroundColor: checked ? '#1B23B8' : '#D9D4C5',
      }}
    >
      <span
        className="inline-block h-5 w-5 rounded-full bg-white transition-transform shadow-sm"
        style={{
          transform: checked ? 'translateX(22px)' : 'translateX(2px)',
        }}
      />
    </button>
  );
}
